import { useState, useEffect } from "react"
import { motion, useMotionValue, useTransform, animate } from "framer-motion"
import { ChevronLeft, ChevronRight, ArrowRight, Users, AlertTriangle, UserX } from "lucide-react"

// Local cn helper — no @/lib/utils alias dependency
const cn = (...classes) => classes.filter(Boolean).join(" ")

const COLORS = {
  emerald: { text: "text-emerald-500", bg: "bg-emerald-500", soft: "bg-emerald-500/15", stroke: "stroke-emerald-500", ring: "stroke-emerald-500/10" },
  amber: { text: "text-amber-500", bg: "bg-amber-500", soft: "bg-amber-500/15", stroke: "stroke-amber-500", ring: "stroke-amber-500/10" },
  rose: { text: "text-rose-500", bg: "bg-rose-500", soft: "bg-rose-500/15", stroke: "stroke-rose-500", ring: "stroke-rose-500/10" },
}

const DEFAULT_ICONS = { active: Users, atRisk: AlertTriangle, inactive: UserX }

// Adapted from Vo2MaxCard's single radial-progress design into a 3-page
// swiper — drag left/right, tap the dots, or use the arrow buttons to move
// between Active / At Risk / Inactive. Each page reuses the animated
// count-up + radial ring, just re-themed per category color.
export function LearnerHealthCard({ categories = [], totalLearners, onView, title = "Learner Health" }) {
  const [index, setIndex] = useState(0)
  const active = categories[index]

  const count = useMotionValue(0)
  const rounded = useTransform(count, (v) => Math.round(v))
  const progressValue = useMotionValue(0)

  const radius = 52
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = useTransform(progressValue, (v) => circumference - (v / 100) * circumference)

  useEffect(() => {
    if (!active) return
    const valueAnim = animate(count, active.value, { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] })
    const progressAnim = animate(progressValue, active.percent, { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] })
    return () => {
      valueAnim.stop()
      progressAnim.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index])

  if (!active) return null

  const colors = COLORS[active.color] || COLORS.emerald
  const Icon = active.icon || DEFAULT_ICONS[active.key] || Users

  const goTo = (i) => setIndex(((i % categories.length) + categories.length) % categories.length)

  const handleDragEnd = (_, info) => {
    if (info.offset.x < -40) goTo(index + 1)
    else if (info.offset.x > 40) goTo(index - 1)
  }

  return (
    <div className="relative w-full p-5 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.06] backdrop-blur-sm transition-all duration-500 hover:bg-foreground/[0.04] hover:border-foreground/[0.1] flex flex-col gap-3 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">{title}</span>
        {totalLearners != null && (
          <span className="text-[11px] text-muted-foreground">{totalLearners.toLocaleString()} total</span>
        )}
      </div>

      {/* Swipeable panel */}
      <motion.div
        key={index}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.15}
        onDragEnd={handleDragEnd}
        className="flex flex-col items-center cursor-grab active:cursor-grabbing touch-pan-y"
      >
        <div className="relative flex h-40 w-40 items-center justify-center">
          <svg
            width="140"
            height="140"
            viewBox="0 0 140 140"
            className="-rotate-90"
            role="progressbar"
            aria-valuenow={active.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <circle cx="70" cy="70" r={radius} strokeWidth="9" fill="transparent" className={colors.ring} strokeDasharray="6 9" strokeLinecap="round" />
            <motion.circle
              cx="70"
              cy="70"
              r={radius}
              strokeWidth="9"
              fill="transparent"
              className={colors.stroke}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeLinecap="round"
              style={{ strokeDashoffset }}
            />
          </svg>
          <div className="absolute flex flex-col items-center justify-center">
            <div className={cn("flex h-7 w-7 items-center justify-center rounded-full mb-1", colors.soft)}>
              <Icon size={14} className={colors.text} />
            </div>
            <motion.span className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">{rounded}</motion.span>
            <span className={cn("text-xs font-medium mt-0.5", colors.text)}>{active.label}</span>
          </div>
        </div>

        <div className="mt-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{active.percent}%</span> of total learners
        </div>

        {onView && (
          <button
            onClick={() => onView(active.key)}
            className={cn("mt-3 flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-70", colors.text)}
          >
            {active.ctaLabel || `View ${active.label} Learners`}
            <ArrowRight size={12} />
          </button>
        )}
      </motion.div>

      {/* Pagination controls */}
      <div className="flex items-center justify-center gap-3 mt-1">
        <button onClick={() => goTo(index - 1)} className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition-colors" aria-label="Previous">
          <ChevronLeft size={14} />
        </button>
        <div className="flex items-center gap-1.5">
          {categories.map((c, i) => (
            <button
              key={c.key}
              onClick={() => goTo(i)}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                i === index ? cn("w-4", COLORS[c.color]?.bg || "bg-foreground") : "w-1.5 bg-foreground/15"
              )}
              aria-label={`Go to ${c.label}`}
            />
          ))}
        </div>
        <button onClick={() => goTo(index + 1)} className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition-colors" aria-label="Next">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

export default LearnerHealthCard